import React, { useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Star, ChevronLeft, ChevronRight, Heart, Sparkles } from 'lucide-react';
import one from "../assets/testimonials/01.mp4";
import two from "../assets/testimonials/02.mp4";
import three from "../assets/testimonials/03.mp4";
import four from "../assets/testimonials/04.mp4";
import five from "../assets/testimonials/05.mp4";
import six from "../assets/testimonials/06.mp4";

function Testimonial() {
    const [activeVideo, setActiveVideo] = useState(null);
    const [muted, setMuted] = useState({});
    const [playing, setPlaying] = useState({});
    const videoRefs = useRef([]);

    const testimonials = [
        {
            id: 1,
            video: five,

        }
        ,
        {
            id: 2,
            video: two,

        },
        {
            id: 3,
            video: three,

        },
        {
            id: 4,
            video: four,

        },
        {
            id: 5,
            video: one,

        },
        {
            id: 6,
            video: six,

        },

    ];

    const handleVideoHover = (index) => {
        if (!playing[index]) {
            videoRefs.current[index]?.play();
            setPlaying(prev => ({ ...prev, [index]: true }));
        }
    };

    const handleVideoLeave = (index) => {
        if (playing[index] && activeVideo !== index) {
            videoRefs.current[index]?.pause();
            videoRefs.current[index].currentTime = 0;
            setPlaying(prev => ({ ...prev, [index]: false }));
        }
    };

    const togglePlay = (index, e) => {
        e.stopPropagation();
        if (playing[index]) {
            videoRefs.current[index]?.pause();
            setPlaying(prev => ({ ...prev, [index]: false }));
            setActiveVideo(null);
        } else {
            videoRefs.current.forEach((ref, i) => {
                if (i !== index && ref) {
                    ref.pause();
                }
            });
            videoRefs.current[index]?.play();
            setPlaying(prev => ({ ...prev, [index]: true }));
            setActiveVideo(index);
        }
    };

    const toggleMute = (index, e) => {
        e.stopPropagation();
        const video = videoRefs.current[index];
        if (video) {
            video.muted = !video.muted;
            setMuted(prev => ({ ...prev, [index]: video.muted }));
        }
    };

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={16}
                className={`${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} transition-colors`}
            />
        ));
    };

    return (
        <div className="relative bg-gradient-to-br from-slate-50 via-white to-green-50 py-20 md:py-28 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="absolute top-20 right-20 opacity-20 hidden lg:block">
                <Sparkles className="w-8 h-8 text-green-400 animate-pulse" />
            </div>
            <div className="absolute bottom-20 left-20 opacity-20 hidden lg:block">
                <Sparkles className="w-12 h-12 text-green-400 animate-pulse animation-delay-2000" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
               
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 via-green-100 to-green-100 rounded-full mb-6 shadow-sm">
                        <Heart className="w-4 h-4 text-green-600 fill-current" />
                        <span className="text-sm font-semibold text-green-700 tracking-wide">
                            CLIENT TESTIMONIALS
                        </span>
                        <Heart className="w-4 h-4 text-green-600 fill-current" />
                    </div>

                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                        <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                            What Our
                        </span>
                        <br />
                        <span className="bg-gradient-to-r from-green-600 via-green-600 to-green-600 bg-clip-text text-transparent">
                            Clients Say
                        </span>
                    </h2>

                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Real stories from real clients. Watch their experiences and see why they love working with us.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {testimonials.map((testimonial, index) => (
                        <div
                            key={testimonial.id}
                            className={`group relative bg-white rounded-2xl overflow-hidden shadow-xl 
                                      hover:shadow-2xl transition-all duration-500 
                                      transform hover:-translate-y-2
                                      ${index === 2 ? 'lg:col-span-1' : ''}
                                      border border-green-200 hover:border-transparent`}
                            onMouseEnter={() => handleVideoHover(index)}
                            onMouseLeave={() => handleVideoLeave(index)}
                        >
                            {/* Video Container */}
                            <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
                                <video
                                    ref={el => videoRefs.current[index] = el}
                                    src={testimonial.video}
                                    className="w-full h-full object-cover cursor-pointer transform group-hover:scale-105 transition-transform duration-700"
                                    loop
                                    muted={muted[index] ?? true}
                                    playsInline
                                    onClick={(e) => togglePlay(index, e)}
                                />

                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                {/* Play/Pause Button */}
                                <button
                                    onClick={(e) => togglePlay(index, e)}
                                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                                             bg-white/90 rounded-full p-4 shadow-lg
                                             opacity-0 group-hover:opacity-100 transition-all duration-300
                                             hover:scale-110 hover:bg-white
                                             backdrop-blur-sm"
                                >
                                    {playing[index] ? (
                                        <Pause className="w-6 h-6 text-green-600" />
                                    ) : (
                                        <Play className="w-6 h-6 text-green-600" />
                                    )}
                                </button>

                                <button
                                    onClick={(e) => toggleMute(index, e)}
                                    className="absolute bottom-3 right-3 bg-black/50 rounded-full p-2
                                             opacity-0 group-hover:opacity-100 transition-all duration-300
                                             hover:bg-black/70 backdrop-blur-sm"
                                >
                                    {muted[index] ? (
                                        <VolumeX className="w-4 h-4 text-white" />
                                    ) : (
                                        <Volume2 className="w-4 h-4 text-white" />
                                    )}
                                </button>

                                {/* Video Duration Indicator */}
                                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800">
                                    <div className="h-full bg-gradient-to-r from-green-500 to-green-500 w-0 group-hover:w-full transition-all duration-300"></div>
                                </div>
                            </div>



                            {/* Decorative Elements */}
                            <div className="absolute top-3 left-3">
                                <div className="flex -space-x-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                                        <span className="text-white text-xs font-bold">{testimonial.id}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="bg-white/80  backdrop-blur-sm rounded-2xl p-6 text-center border border-green-200">
                        <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent mb-2">500+</div>
                        <div className="text-gray-600">Happy Clients</div>
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center border border-green-200">
                        <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent mb-2">98%</div>
                        <div className="text-gray-600">Satisfaction Rate</div>
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center border border-green-200">
                        <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent mb-2">50+</div>
                        <div className="text-gray-600">Video Testimonials</div>
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center border border-green-200">
                        <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent mb-2">5★</div>
                        <div className="text-gray-600">Average Rating</div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                
                .animate-blob {
                    animation: blob 7s infinite;
                }
                
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </div>
    );
}

export default Testimonial;